import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGlobalState } from '../context/GlobalState';
import { deusSocket } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { initializeCaptcha, resetCaptcha } from '../utils/captcha';
import './AuthScreen.css'; // Reuse Auth Screen styling

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { signInUser } = useGlobalState();
  
  // Verification states
  const [verificationStatus, setVerificationStatus] = useState('pending');
  const [message, setMessage] = useState('Verifying your email address...');
  const [membershipLevel, setMembershipLevel] = useState(null);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  
  // Extract token and userId once and store them in refs to avoid re-renders
  const tokenRef = useRef(searchParams.get('token'));
  const userIdRef = useRef(searchParams.get('userId'));

  // Initialize captcha when email input form is shown
  useEffect(() => {
    let cleanup = null;
    
    if (showEmailInput) {
      cleanup = initializeCaptcha(
        // On token change
        (token) => setCaptchaToken(token),
        // On error
        (errorMsg) => setEmailError(errorMsg)
      );
    }
    
    return () => {
      if (cleanup) cleanup();
    };
  }, [showEmailInput]);

  // Ensure the socket is connected
  const ensureSocketConnection = () => {
    if (!deusSocket.connected) {
      deusSocket.connect();
      
      // Return a promise that resolves when the socket is connected
      return new Promise((resolve) => {
        const onConnect = () => {
          deusSocket.off('connect', onConnect);
          resolve();
        };
        
        deusSocket.on('connect', onConnect);
        
        // If already connected, resolve immediately
        if (deusSocket.connected) {
          deusSocket.off('connect', onConnect);
          resolve();
        }
      });
    }
    
    // If already connected, return a resolved promise
    return Promise.resolve();
  };

  // Simpler verification function - directly triggered in useEffect
  const performVerification = async () => {
    // Basic validation
    if (!tokenRef.current || !userIdRef.current) {
      setVerificationStatus('error');
      setMessage('Invalid verification link. Missing required parameters.');
      return;
    }
    
    try {
      // Ensure socket is connected
      await ensureSocketConnection();
      // Set up handlers for verification response
      const handleEmailVerified = (user) => {
        // Update UI state
        setVerificationStatus('success');
        setMessage('Your email has been successfully verified! You can now access your dashboard.');
        setMembershipLevel(user.membership || 'free');
        
        // Store user data
        localStorage.setItem('token', user.token);
        localStorage.setItem('user', user._id.toString());
        
        // Sign the user in
        signInUser(user);
        
        // Redirect to the app after a short delay
        setTimeout(() => {
          navigate('/app');
        }, 3000);
        
        // Clean up
        deusSocket.off('emailVerified', handleEmailVerified);
        deusSocket.off('error-event', handleErrorEvent);
      };
      
      const handleErrorEvent = (err) => {
        setVerificationStatus('error');
        setMessage(err.message === 'Invalid verification token or user ID' 
          ? 'Invalid or expired verification link.' 
          : `An error occurred: ${err.message}`);
          
        // Clean up
        deusSocket.off('emailVerified', handleEmailVerified);
        deusSocket.off('error-event', handleErrorEvent);
      };
      
      // Set up socket listeners
      deusSocket.on('emailVerified', handleEmailVerified);
      deusSocket.on('error-event', handleErrorEvent);
      
      // Add timeout for no response
      const timeoutId = setTimeout(() => {
        deusSocket.off('emailVerified', handleEmailVerified);
        deusSocket.off('error-event', handleErrorEvent);
        
        setVerificationStatus('error');
        setMessage('Verification timed out. Please try again or contact support.');
      }, 15000);
      
      // CRITICAL: Force a small delay to ensure socket is fully ready
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Emit verification request - CRITICAL PART
      deusSocket.emit('verifyEmail', { 
        token: tokenRef.current, 
        userId: userIdRef.current 
      }, (response) => {
        clearTimeout(timeoutId);
      });
      
    } catch (error) {
      setVerificationStatus('error');
      setMessage('Failed to connect to the verification service. Please try again later.');
    }
  };

  // Simplified useEffect - less conditions to interfere with verification
  useEffect(() => {
    
    // Immediately check if we have required parameters
    if (!tokenRef.current || !userIdRef.current) {
      console.error('Missing required verification parameters');
      setVerificationStatus('error');
      setMessage('Invalid verification link. Missing required parameters.');
      return;
    }
    
    // Wait for a small delay to ensure everything is initialized
    const timer = setTimeout(() => {
      performVerification();
    }, 1000);
    
    return () => {
      clearTimeout(timer);
      deusSocket.off('emailVerified');
      deusSocket.off('error-event');
    };
  }, []);

  const handleResendVerification = () => {
    // Show the email input form instead of directly sending
    setShowEmailInput(true);
    setEmailError('');
  };

  const submitEmailForResend = async (e) => {
    e.preventDefault();
    
    // Basic email validation
    if (!email || !email.includes('@') || !email.includes('.')) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    // Verify captcha is completed
    if (!captchaToken) {
      setEmailError('Please complete the captcha verification');
      return;
    }
    
    const userId = searchParams.get('userId');
    if (!userId) {
      setEmailError('User ID is missing. Please go back to the sign-up page.');
      return;
    }
    
    setResendingVerification(true);
    setEmailError('');
    
    try {
      // Ensure socket is connected
      await ensureSocketConnection();
    
      // Set up handlers with direct approach
      deusSocket.once('verificationEmailSent', () => {
        setResendingVerification(false);
        setShowEmailInput(false);
        setResendMessage('Verification email has been sent! Please check your inbox.');
      });
      
      deusSocket.once('error-event', (err) => {
        setResendingVerification(false);
        setEmailError(`Failed to resend: ${err.message || 'Unknown error'}`);
        resetCaptcha(); // Reset captcha on error
      });
      
      // Set a direct timeout
      const timeoutId = setTimeout(() => {
        setResendingVerification(false);
        setEmailError('Request timed out. Please try again later.');
        deusSocket.off('verificationEmailSent');
        deusSocket.off('error-event');
        resetCaptcha(); // Reset captcha on timeout
      }, 10000);
      
      // Emit resend verification request with captcha token
      deusSocket.emit('sendVerificationEmail', { 
        userId: userId,
        email,
        captchaToken
      });
      
      // Clear timeout after some time regardless
      setTimeout(() => {
        clearTimeout(timeoutId);
      }, 10000);
      
    } catch (error) {
      // Update UI on error
      console.error('Error in resend process:', error);
      setResendingVerification(false);
      setEmailError(`Failed to resend: ${error.message || 'Unknown error'}`);
      resetCaptcha(); // Reset captcha on error
    }
  };

  const renderMembershipInfo = () => {
    if (!membershipLevel) return null;
    
    const membershipDisplay = {
      free: 'Free',
      plus: 'Starter',
      pro: 'Pro',
      god: 'God'
    };
    
    return (
      <div className="membership-info">
        <p>Your account has been created with the <strong>{membershipDisplay[membershipLevel]}</strong> plan.</p>
        <p>You can start creating your mind clones right away!</p>
      </div>
    );
  };

  return (
    <div className="auth-screen-container">
      <div className="auth-screen-card">
        {verificationStatus === 'pending' && (
          <div className="auth-loading">
            <LoadingSpinner />
            <p>{message}</p>
          </div>
        )}
        
        {verificationStatus === 'success' && (
          <div className="auth-success">
            <svg viewBox="0 0 24 24" className="auth-icon">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"></path>
            </svg>
            <p>{message}</p>
            
            {renderMembershipInfo()}
            
            <div className="redirect-message">
              <p>Redirecting you to the app in a few seconds...</p>
              <button 
                className="auth-button primary" 
                onClick={() => navigate('/app')}
              >
                Go to Dashboard Now
              </button>
            </div>
          </div>
        )}
        
        {verificationStatus === 'error' && (
          <div className="auth-error">
            <svg viewBox="0 0 24 24" className="auth-icon">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"></path>
            </svg>
            <p>{message}</p>
            
            {resendMessage && (
              <div className={`resend-message ${resendMessage.includes('Failed') ? 'error' : 'success'}`}>
                {resendMessage}
              </div>
            )}
            
            {showEmailInput ? (
              <form onSubmit={submitEmailForResend} className="resend-form">
                <p>Please enter your email address to receive a new verification link:</p>
                <div className="form-group">
                  <input
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  {emailError && <div className="email-error">{emailError}</div>}
                </div>
                
                {/* Add reCAPTCHA container */}
                <div className="captcha-container">
                  <div 
                    id="g-recaptcha-container"
                    className="g-recaptcha" 
                    data-sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"} 
                    data-callback="onCaptchaComplete"
                    data-error-callback="onCaptchaError"
                  ></div>
                  {!captchaToken && emailError && emailError.includes('captcha') && (
                    <div className="captcha-error">Please complete the captcha verification</div>
                  )}
                </div>
                
                <div className="auth-error-actions">
                  <button 
                    type="submit" 
                    className="auth-button primary"
                    disabled={resendingVerification}
                  >
                    {resendingVerification ? <LoadingSpinner size="small" /> : 'Send Verification Email'}
                  </button>
                  <button 
                    type="button" 
                    className="auth-button secondary"
                    onClick={() => {
                      setShowEmailInput(false);
                      resetCaptcha();
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="auth-error-actions">
                <button 
                  className="auth-button secondary" 
                  onClick={handleResendVerification}
                >
                  Resend Verification Email
                </button>
                
                <button 
                  className="auth-button primary" 
                  onClick={() => navigate('/auth')}
                >
                  Return to Login
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail; 